import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { BaseIndexPageComponent } from "../../../components/pages/base-index-page/base-index-page.component";
import BaseIndexPageConfig from '../../../components/pages/base-index-page/base-index-page.config';
import { UserService } from '../../../services/api/user.service';
import { AuthService } from '../../../services/api/auth.service';
import ApiOptionsConfig from '../../../services/api/api-options.config';
import { RouteService } from '../../../services/route.service';
import { StoreService } from '../../../services/store.service';
import { MainLayoutService } from '../../../services/main-layout.service';
import { AlertNotificationService } from '../../../services/alert-notification.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { DataTableColumnText } from '../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnActions } from '../../../components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import { filter, forkJoin, take } from 'rxjs';

@Component({
  selector: 'app-users-index-page',
  imports: [BaseIndexPageComponent],
  providers: [],
  templateUrl: './users-index-page.component.html',
  styleUrl: './users-index-page.component.scss'
})
export class UsersIndexPageComponent {
  list: any[] = [];

  baseIndexPageConfig: BaseIndexPageConfig;

  private currentUserId: any = null;

  constructor(
    protected userService: UserService,
    private routeService: RouteService,
    private authService: AuthService,
    private storeService: StoreService,
    private mainLayoutService: MainLayoutService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService,
    private router: Router
  ) {
    this.storeService.getUserData().pipe(take(1)).subscribe((user: any) => {
      this.currentUserId = user?.userId ?? null;
    });

    this.baseIndexPageConfig = {
      pageTitle: 'Users',
      cardTitle: 'Manage Users',
      addNewItemButton: {
        type: 'success',
        title: 'Add User',
        onClick: () => {
          this.routeService.userCreate()
          console.log('add user tapped');
        }
      },
      dataTable: {
        data: [],
        searchModel: {
          userId: null,
          firstName: null,
          lastName: null,
        },
        pagination: null,
        columns: [
          new DataTableColumnText({
            attribute: 'userId',
            header: 'User ID',
            width: '70px',
            filter: 'text',
            value: (item: any) => {
              return item.userId;
            }
          }),

          new DataTableColumnText({
            attribute: 'userCode',
            header: 'User Code',
            filter: 'text',
            value: (item: any) => {
              return item.userCode;
            }
          }),

          new DataTableColumnText({
            attribute: 'profile',
            header: 'Profile',
            filter: 'select',
            filterOptionsSource: 'userProfile',
            value: (item: any) => {
              return item.userProfiles.map((profile: any) => profile.profile).join(', ');
            }
          }),

          new DataTableColumnText({
            attribute: 'companyName',
            header: 'Company',
            filter: 'text',
            width: '150px',
            value: (item: any) => {
              const companyNames = item.userProfiles?.map((profile: any) => profile?.company?.name).filter((name: any) => !!name);
              return Array.from(new Set(companyNames)).join(', ');
            }
          }),

          new DataTableColumnText({
            attribute: 'customerCompanyName',
            header: 'Cust. Company',
            filter: 'text',
            width: '150px',
            value: (item: any) => {
              return item.userProfiles?.map((profile: any) => profile?.customerPerson?.customer?.companyName).join(', ');
            }
          }),

          new DataTableColumnText({
            attribute: 'firstName',
            header: 'First name',
            filter: 'text',
            value: (item: any) => {
              return item.firstName;
            }
          }),
          new DataTableColumnText({
            attribute: 'lastName',
            header: 'Last name',
            filter: 'text',
            value: (item: any) => {
              return item.lastName;
            }
          }),
          new DataTableColumnText({
            attribute: 'email',
            header: 'Email',
            filter: 'text',
            value: (item: any) => {
              return item.email;
            }
          }),
          new DataTableColumnText({
            attribute: 'status',
            header: 'Status',
            filter: 'select',
            filterOptionsSource: 'userStatus',
            value: (item: any) => {
              return item.status;
            }
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            style: { width: '50px' },
            buttons: [
              {
                icon: 'fa-user-secret',
                tooltip: 'Impersonate user',
                aclAction: 'impersonate',
                hidden: (item: any) => item.userId === this.currentUserId,
                onClick: (item: any) => {
                  this.confirmationDialogService.open(
                    `Are you sure you want to impersonate ${item.firstName} ${item.lastName}?`,
                    () => this.onImpersonate(item)
                  );
                }
              },
              {
                icon: 'fa-eye',
                tooltip: 'View user details',
                onClick: (item: any) => {
                  this.routeService.userDetail(item.userId)
                }
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit user',
                onClick: (item: any) => {
                  this.routeService.userUpdate(item.userId)
                }
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete user',
                onClick: (item: any) => {
                },
                isDelete: true
              }

            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.userService.list(options),
      responseListModelName: 'users',
    }

  }

  onImpersonate(item: any): void {
    forkJoin([
      this.storeService.getUserData().pipe(take(1)),
      this.storeService.getAuthToken().pipe(take(1))
    ]).subscribe(([originalUser, originalAuth]: [any, any]) => {
      this.authService.impersonate(item.userId).subscribe({
        next: (response: any) => {
          if (response.status.code !== 200) {
            const errorMessage = response.status.error || 'Impersonation failed.';
            this.alertNotificationService.error(errorMessage);
            return;
          }

          this.storeService.setAuthToken(response.data.auth);

          this.userService.getMe().subscribe({
            next: (userResponse: any) => {
              if (userResponse.status.code === 200) {
                const user = userResponse?.data?.user;

                if (!this.mainLayoutService.isImpersonating() && originalUser && originalAuth) {
                  this.mainLayoutService.startImpersonation(originalUser, originalAuth);
                }

                this.router.navigate(['/app']).then(() => {
                  this.storeService.setUserData(user);
                  this.mainLayoutService.setUser(user);
                  localStorage.setItem('user', JSON.stringify(user));

                  const hasCompany = user?.userProfiles?.some((profile: any) => !!profile?.company);
                  if (hasCompany) {
                    this.mainLayoutService.refreshCompany();
                  }

                  this.alertNotificationService.success(`You are now impersonating ${item.firstName} ${item.lastName}.`);
                });
              }
            },
            error: (error) => {
              const errorMessage = error?.error?.error?.description || 'Failed to fetch impersonated user data.';
              this.alertNotificationService.error(errorMessage);
            }
          });
        },
        error: (error) => {
          const errorMessage = error?.error?.error?.description || 'Failed to impersonate user.';
          this.alertNotificationService.error(errorMessage);
        }
      });
    });
  }

}





